import type {
	ErrorMessage,
	SuccessMessage,
} from "@elaso/shared/types/web-socket";
import {
	addBreadcrumb,
	captureException,
	captureMessage,
	logger as sentryLogger,
} from "@sentry/react";
import { SENTRY_ENABLED } from "@/lib/sentry";

type WebSocketWrapperOptions = {
	autoConnect?: boolean;
	reconnectInterval?: number;
	maxReconnectInterval?: number;
	reconnectBackoffMultiplier?: number;
	debug?: boolean;
};

const DEFAULT_RECONNECT_INTERVAL = 1000;
const DEFAULT_MAX_RECONNECT_INTERVAL = 300_000;
const DEFAULT_BACKOFF_MULTIPLIER = 1.5;
const RECONNECT_LOOP_THRESHOLD = 5;
const PENDING_CALL_PROCEDURES_CAP = 20;

class WebSocketWrapper extends EventTarget {
	private ws: WebSocket | null = null;
	private wsUrl: string;
	private readonly wsProtocols?: string | string[];
	private readonly options: Required<WebSocketWrapperOptions>;
	private reconnectAttempts = 0;
	private reconnectTimeoutId: number | null = null;
	private isManuallyDisconnected = false;
	private messageQueue: Array<
		string | ArrayBufferLike | Blob | ArrayBufferView
	> = [];
	private connecting = false;
	private pendingCalls = new Map<
		string,
		{
			resolve: (value: any) => void;
			reject: (reason: any) => void;
			procedure: string;
			/** The original request payload, kept so a recoverable failure
			 * (see _recoverWorkspace) can transparently re-send it once. */
			request?: {
				id: string;
				procedure: string;
				data?: any;
				timeoutMs?: number;
			};
			/** Guards the one-shot retry so a recovery can't loop. */
			retried?: boolean;
		}
	>();
	private _beforeReconnect: (() => Promise<void>) | null = null;
	/** Optional app-level hook to re-establish the server-side session context
	 * (workspace) when a request fails with WORKSPACE_REQUIRED. When set, such a
	 * request is retried ONCE after this resolves, so a desynced session
	 * (alive socket, no workspace set) self-heals instead of surfacing an error. */
	private _recoverWorkspace: (() => Promise<void>) | null = null;
	private currentConnectionAttemptId: string | null = null;
	private consecutiveFailureStreak = 0;
	private loopEventFiredForCurrentStreak = false;
	private lastCloseCode: number | null = null;
	private lastCloseReason: string | null = null;
	private hasOpened = false;
	private connectStartedAt: number | null = null;
	private pendingErrorPayload: {
		baseError: Error;
		details: Record<string, unknown>;
	} | null = null;
	private pendingErrorFlushTimeoutId: number | null = null;
	private static readonly PENDING_ERROR_FLUSH_MS = 2000;
	private static readonly SENSITIVE_QUERY_PARAMS = [
		"token",
		"access_token",
		"auth",
	];

	constructor(
		url: string,
		protocols?: string | string[],
		options: WebSocketWrapperOptions = {}
	) {
		super();
		this.wsUrl = url;
		this.wsProtocols = protocols;
		this.options = {
			autoConnect: options.autoConnect ?? false,
			reconnectInterval:
				options.reconnectInterval ?? DEFAULT_RECONNECT_INTERVAL,
			maxReconnectInterval:
				options.maxReconnectInterval ?? DEFAULT_MAX_RECONNECT_INTERVAL,
			reconnectBackoffMultiplier:
				options.reconnectBackoffMultiplier ?? DEFAULT_BACKOFF_MULTIPLIER,
			debug: options.debug ?? false,
		};

		if (typeof document !== "undefined") {
			document.addEventListener("visibilitychange", () => {
				if (
					document.visibilityState === "visible" &&
					!this.isManuallyDisconnected &&
					!this.isConnected &&
					!this.isConnecting
				) {
					this.log("Tab became visible, triggering immediate reconnect");
					if (this.reconnectTimeoutId) {
						clearTimeout(this.reconnectTimeoutId);
						this.reconnectTimeoutId = null;
					}
					this.reconnectAttempts = 0;
					this.scheduleReconnect();
				}
			});
		}

		if (this.options.autoConnect) {
			this.connect();
		}
	}

	private log(...args: unknown[]) {
		if (this.options.debug) {
			console.log("[WebSocketWrapper]", ...args);
		}
	}

	private getSanitizedUrl(input?: string): string {
		const raw = input ?? this.ws?.url ?? this.wsUrl;
		if (!raw) {
			return "";
		}
		try {
			const parsed = new URL(raw);
			for (const key of WebSocketWrapper.SENSITIVE_QUERY_PARAMS) {
				if (parsed.searchParams.has(key)) {
					parsed.searchParams.set(key, "[redacted]");
				}
			}
			return parsed.toString();
		} catch {
			return raw.replace(
				/([?&](?:token|access_token|auth)=)[^&]*/gi,
				"$1[redacted]"
			);
		}
	}

	private parseTokenInfo(): {
		tokenExpInSeconds: number | null;
		tokenAgeSeconds: number | null;
		tokenParseFailed: boolean;
	} {
		try {
			const parsed = new URL(this.wsUrl);
			const token = parsed.searchParams.get("token");
			if (!token) {
				return {
					tokenExpInSeconds: null,
					tokenAgeSeconds: null,
					tokenParseFailed: true,
				};
			}
			const segments = token.split(".");
			if (segments.length < 2) {
				return {
					tokenExpInSeconds: null,
					tokenAgeSeconds: null,
					tokenParseFailed: true,
				};
			}
			const padded = segments[1].replace(/-/g, "+").replace(/_/g, "/");
			const json = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
			const claims = JSON.parse(json) as { exp?: number; iat?: number };
			const nowSeconds = Math.floor(Date.now() / 1000);
			return {
				tokenExpInSeconds:
					typeof claims.exp === "number" ? claims.exp - nowSeconds : null,
				tokenAgeSeconds:
					typeof claims.iat === "number" ? nowSeconds - claims.iat : null,
				tokenParseFailed: false,
			};
		} catch {
			return {
				tokenExpInSeconds: null,
				tokenAgeSeconds: null,
				tokenParseFailed: true,
			};
		}
	}

	private getNetworkSnapshot(): Record<string, unknown> {
		const snapshot: Record<string, unknown> = {};
		try {
			if (typeof navigator !== "undefined") {
				snapshot.navigatorOnLine = navigator.onLine;
			}
		} catch {}
		try {
			if (
				typeof performance !== "undefined" &&
				typeof performance.getEntriesByType === "function"
			) {
				const host = (() => {
					try {
						return new URL(this.wsUrl).host;
					} catch {
						return null;
					}
				})();
				if (host) {
					const entries = performance.getEntriesByType("resource");
					snapshot.wsResourceEntryCount = entries.filter((e) =>
						e.name.includes(host)
					).length;
				}
			}
		} catch {}
		return snapshot;
	}

	private getReadyStateLabel(state: number): string {
		switch (state) {
			case WebSocket.CONNECTING:
				return "CONNECTING";
			case WebSocket.OPEN:
				return "OPEN";
			case WebSocket.CLOSING:
				return "CLOSING";
			default:
				return "CLOSED";
		}
	}

	private getPendingCallProcedures(): Array<{ id: string; procedure: string }> {
		const out: Array<{ id: string; procedure: string }> = [];
		for (const [id, pending] of this.pendingCalls) {
			if (out.length >= PENDING_CALL_PROCEDURES_CAP) {
				break;
			}
			out.push({ id, procedure: pending.procedure });
		}
		return out;
	}

	private getErrorDetails(event: ErrorEvent) {
		const maybeError = event.error instanceof Error ? event.error : undefined;

		return {
			readyState: this.readyState,
			readyStateLabel: this.getReadyStateLabel(this.readyState),
			url: this.getSanitizedUrl(),
			protocol: this.protocol,
			extensions: this.extensions,
			bufferedAmount: this.bufferedAmount,
			queuedMessages: this.queuedMessages,
			pendingCallsCount: this.pendingCalls.size,
			pendingCallProcedures: this.getPendingCallProcedures(),
			isManuallyDisconnected: this.isManuallyDisconnected,
			errorMessage: event.message || undefined,
			errorFilename: event.filename || undefined,
			errorLine: event.lineno || undefined,
			errorColumn: event.colno || undefined,
			errorName: maybeError?.name,
			errorStack: maybeError?.stack,
			...this.getNetworkSnapshot(),
		};
	}

	private getBaseError(event: ErrorEvent): Error {
		if (event.error instanceof Error) {
			return event.error;
		}

		if (event.message) {
			return new Error(event.message);
		}

		return new Error("WebSocket connection error");
	}

	private getMinimalErrorDetails() {
		return {
			readyState: this.readyState,
			readyStateLabel: this.getReadyStateLabel(this.readyState),
			url: this.getSanitizedUrl(),
			protocol: this.protocol,
			extensions: this.extensions,
			bufferedAmount: this.bufferedAmount,
			queuedMessages: this.queuedMessages,
			pendingCallsCount: this.pendingCalls.size,
			isManuallyDisconnected: this.isManuallyDisconnected,
		};
	}

	private getSafeErrorPayload(event: ErrorEvent): {
		baseError: Error;
		details: Record<string, unknown>;
	} {
		const baseError = this.getBaseError(event);

		try {
			return {
				baseError,
				details: this.getErrorDetails(event),
			};
		} catch (enhancementError) {
			return {
				baseError,
				details: {
					...this.getMinimalErrorDetails(),
					enhancementFailed: true,
					enhancementError:
						enhancementError instanceof Error
							? {
									name: enhancementError.name,
									message: enhancementError.message,
									stack: enhancementError.stack,
								}
							: String(enhancementError),
				},
			};
		}
	}

	private captureWebSocketError(
		baseError: Error,
		details: Record<string, unknown>
	) {
		if (!SENTRY_ENABLED) {
			return;
		}

		const tokenInfo = this.parseTokenInfo();
		const phase: "connecting" | "open" = this.hasOpened ? "open" : "connecting";
		const enrichedExtra = {
			...details,
			...tokenInfo,
		};

		const attemptIdTag = this.currentConnectionAttemptId ?? "unknown";
		try {
			captureException(baseError, {
				tags: {
					context: "websocket_error",
					phase,
					attempt: String(this.reconnectAttempts),
					clientConnectionAttemptId: attemptIdTag,
				},
				extra: enrichedExtra,
			});
		} catch (captureError) {
			this.log("Failed to capture enhanced websocket error", captureError);

			try {
				captureException(new Error("WebSocket connection error (fallback)"), {
					tags: {
						context: "websocket_error",
						capture_mode: "fallback",
						phase,
						attempt: String(this.reconnectAttempts),
						clientConnectionAttemptId: attemptIdTag,
					},
					extra: {
						...this.getMinimalErrorDetails(),
						...tokenInfo,
						originalErrorMessage: baseError.message,
						captureError:
							captureError instanceof Error
								? {
										name: captureError.name,
										message: captureError.message,
										stack: captureError.stack,
									}
								: String(captureError),
					},
				});
			} catch (fallbackCaptureError) {
				this.log(
					"Failed to capture websocket error fallback",
					fallbackCaptureError
				);
			}
		}
	}

	private flushPendingError(closeInfo?: {
		closeCode: number;
		closeReason: string;
		closeWasClean: boolean;
	}) {
		if (this.pendingErrorFlushTimeoutId !== null) {
			clearTimeout(this.pendingErrorFlushTimeoutId);
			this.pendingErrorFlushTimeoutId = null;
		}
		const payload = this.pendingErrorPayload;
		if (!payload) {
			return;
		}
		this.pendingErrorPayload = null;
		const details = closeInfo
			? { ...payload.details, ...closeInfo }
			: payload.details;
		this.captureWebSocketError(payload.baseError, details);
	}

	private setupEventListeners() {
		if (!this.ws) {
			return;
		}

		this.ws.addEventListener("open", () => {
			this.log("Connected");
			this.connecting = false;
			this.hasOpened = true;
			const attemptForLog = this.reconnectAttempts;
			this.reconnectAttempts = 0;
			this.consecutiveFailureStreak = 0;
			this.loopEventFiredForCurrentStreak = false;
			const durationMs =
				this.connectStartedAt !== null
					? Math.round(performance.now() - this.connectStartedAt)
					: null;
			this.connectStartedAt = null;
			const attemptId = this.currentConnectionAttemptId;
			if (SENTRY_ENABLED) {
				try {
					addBreadcrumb({
						category: "websocket",
						message: "handshake_succeeded",
						level: "info",
						data: { durationMs, clientConnectionAttemptId: attemptId },
					});
				} catch {}
				try {
					sentryLogger.info("WebSocket connected", {
						durationMs,
						attempt: attemptForLog,
						clientConnectionAttemptId: attemptId,
					});
				} catch {}
			}
			this.flushMessageQueue();
			this.dispatchEvent(new Event("open"));
		});

		this.ws.addEventListener("message", (event) => {
			try {
				const parsedMessage = JSON.parse(event.data);
				if (
					parsedMessage.type === "SUCCESS" ||
					parsedMessage.type === "ERROR"
				) {
					const pending = this.pendingCalls.get(parsedMessage.id);
					if (pending) {
						if (parsedMessage.type === "SUCCESS") {
							this.pendingCalls.delete(parsedMessage.id);
							pending.resolve((parsedMessage as SuccessMessage).data);
							return;
						}

						const errMsg = parsedMessage as ErrorMessage;
						const rejectWithCode = () => {
							this.pendingCalls.delete(parsedMessage.id);
							const err = new Error(errMsg.error);
							(err as Error & { code?: string }).code = errMsg.code;
							pending.reject(err);
						};

						// Self-heal a desynced session. The socket is alive but the
						// server connection has no workspace set (e.g. a reconnect or
						// bootstrap that didn't re-send workspace.setCurrent). Recover the
						// workspace context, then re-send the original request ONCE so the
						// action succeeds transparently instead of failing. The
						// workspace.setCurrent call itself is excluded to avoid a loop.
						if (
							errMsg.code === "WORKSPACE_REQUIRED" &&
							!pending.retried &&
							!!this._recoverWorkspace &&
							!!pending.request &&
							pending.request.procedure !== "workspace.setCurrent"
						) {
							pending.retried = true;
							this.log(
								"WORKSPACE_REQUIRED — recovering workspace context, retrying once"
							);
							this._recoverWorkspace()
								.then(() => {
									if (
										this.pendingCalls.has(parsedMessage.id) &&
										pending.request
									) {
										this.send(JSON.stringify(pending.request));
									}
								})
								.catch((recoverErr) => {
									this.log("workspace recovery failed:", recoverErr);
									rejectWithCode();
								});
							return;
						}

						rejectWithCode();
						return;
					}
				}
			} catch {}

			this.dispatchEvent(
				new MessageEvent("message", {
					data: event.data,
					origin: event.origin,
					lastEventId: event.lastEventId,
					source: event.source,
					ports: [...event.ports],
				})
			);
		});

		this.ws.addEventListener("error", (event) => {
			const errorEvent = event as ErrorEvent;
			const { baseError, details } = this.getSafeErrorPayload(errorEvent);

			this.log("WebSocket error occurred", details);
			sentryLogger.error("WebSocket error", {
				message: baseError.message,
				readyState: details.readyState,
				pendingCallsCount: details.pendingCallsCount,
			});
			this.connecting = false;

			this.pendingErrorPayload = { baseError, details };
			if (this.pendingErrorFlushTimeoutId !== null) {
				clearTimeout(this.pendingErrorFlushTimeoutId);
			}
			this.pendingErrorFlushTimeoutId = window.setTimeout(() => {
				this.flushPendingError();
			}, WebSocketWrapper.PENDING_ERROR_FLUSH_MS);

			this.dispatchEvent(new Event("error"));
		});

		this.ws.addEventListener("close", (event) => {
			this.log("Connection closed:", event.code, event.reason);
			this.connecting = false;
			this.lastCloseCode = event.code;
			this.lastCloseReason = event.reason;

			if (this.pendingErrorPayload) {
				this.flushPendingError({
					closeCode: event.code,
					closeReason: event.reason,
					closeWasClean: event.wasClean,
				});
			}

			if (!this.isManuallyDisconnected) {
				this.consecutiveFailureStreak += 1;
				if (
					this.consecutiveFailureStreak >= RECONNECT_LOOP_THRESHOLD &&
					!this.loopEventFiredForCurrentStreak
				) {
					this.loopEventFiredForCurrentStreak = true;
					if (SENTRY_ENABLED) {
						try {
							captureMessage("websocket_reconnect_loop", {
								level: "warning",
								tags: {
									context: "websocket_reconnect_loop",
									clientConnectionAttemptId:
										this.currentConnectionAttemptId ?? "unknown",
								},
								extra: {
									streak: this.consecutiveFailureStreak,
									threshold: RECONNECT_LOOP_THRESHOLD,
									lastCloseCode: this.lastCloseCode,
									lastCloseReason: this.lastCloseReason,
									attempt: this.reconnectAttempts,
									...this.getMinimalErrorDetails(),
								},
							});
						} catch (err) {
							this.log("Failed to capture reconnect-loop event", err);
						}
					}
				}
			}

			if (this.pendingErrorPayload) {
				this.flushPendingError({
					closeCode: event.code,
					closeReason: event.reason,
					closeWasClean: event.wasClean,
				});
			}

			this.dispatchEvent(
				new CloseEvent("close", {
					code: event.code,
					reason: event.reason,
					wasClean: event.wasClean,
				})
			);

			if (!this.isManuallyDisconnected) {
				this.scheduleReconnect();
			}
		});
	}

	private scheduleReconnect() {
		if (this.isManuallyDisconnected) {
			this.log("Manually disconnected, skipping reconnect");
			return;
		}

		const delay = Math.min(
			this.options.reconnectInterval *
				this.options.reconnectBackoffMultiplier ** this.reconnectAttempts,
			this.options.maxReconnectInterval
		);

		this.log(
			`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`
		);
		sentryLogger.warn("WebSocket reconnecting", {
			attempt: this.reconnectAttempts + 1,
			delayMs: delay,
		});

		this.dispatchEvent(new CustomEvent("reconnecting", { detail: { delay } }));

		this.reconnectTimeoutId = window.setTimeout(async () => {
			this.reconnectAttempts += 1;

			if (this._beforeReconnect) {
				try {
					await this._beforeReconnect();
				} catch (error) {
					this.log("beforeReconnect failed:", error);
					this.scheduleReconnect();
					return;
				}
			}

			this.connect(false);
		}, delay);
	}

	private flushMessageQueue() {
		while (this.messageQueue.length > 0 && this.readyState === WebSocket.OPEN) {
			const message = this.messageQueue.shift();
			if (message && this.ws) {
				this.ws.send(message);
			}
		}
	}

	send(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
		if (this.readyState === WebSocket.OPEN && this.ws) {
			this.ws.send(data);
		} else {
			this.messageQueue.push(data);
			this.log("Message queued (connection not ready)");
		}
	}

	/** Send a message. Resolves on SUCCESS, rejects on ERROR. */
	sendWithPromise<T>(data: {
		id: string;
		procedure: string;
		data?: any;
		/** Override the default 30s timeout. Long-running admin RPCs (e.g. the
		 * platform import flow) pass a larger value here. */
		timeoutMs?: number;
	}): Promise<T> {
		return new Promise((resolve, reject) => {
			this.pendingCalls.set(data.id, {
				resolve,
				reject,
				procedure: data.procedure,
			});

			const timeoutMs = data.timeoutMs ?? 30_000;
			const timeout = setTimeout(() => {
				if (this.pendingCalls.has(data.id)) {
					this.pendingCalls.delete(data.id);
					sentryLogger.error("WebSocket request timeout", {
						callId: data.id,
						procedure: data.procedure,
						timeoutMs,
					});
					if (SENTRY_ENABLED) {
						captureMessage(`WebSocket request timeout for call ${data.id}`, {
							level: "warning",
							extra: { callId: data.id, timeoutMs },
						});
					}
					reject(new Error("Request timeout"));
				}
			}, timeoutMs);

			const originalResolve = resolve;
			const originalReject = reject;
			this.pendingCalls.set(data.id, {
				resolve: (value: any) => {
					clearTimeout(timeout);
					originalResolve(value);
				},
				reject: (reason: any) => {
					clearTimeout(timeout);
					originalReject(reason);
				},
				procedure: data.procedure,
				request: data,
				retried: false,
			});

			this.send(JSON.stringify(data));
		});
	}

	close(code?: number, reason?: string) {
		this.disconnect(code, reason);
	}

	connect(resetAttempts = true) {
		this.isManuallyDisconnected = false;

		if (resetAttempts) {
			this.reconnectAttempts = 0;
		}

		if (this.reconnectTimeoutId) {
			clearTimeout(this.reconnectTimeoutId);
			this.reconnectTimeoutId = null;
		}

		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			this.log("Already connected");
			return;
		}

		if (
			this.connecting ||
			(this.ws && this.ws.readyState === WebSocket.CONNECTING)
		) {
			this.log("Already connecting");
			return;
		}

		this.connecting = true;
		this.hasOpened = false;
		this.connectStartedAt = performance.now();
		this.currentConnectionAttemptId =
			typeof crypto !== "undefined" && "randomUUID" in crypto
				? crypto.randomUUID()
				: `${Date.now()}-${Math.random().toString(36).slice(2)}`;

		let urlToOpen = this.wsUrl;
		try {
			const parsed = new URL(this.wsUrl);
			parsed.searchParams.set(
				"clientConnectionAttemptId",
				this.currentConnectionAttemptId
			);
			urlToOpen = parsed.toString();
		} catch {
			// Malformed URL, so fall back to the original. The WebSocket constructor
			// below throws and the catch reschedules.
		}

		this.log("Connecting to", urlToOpen);

		if (SENTRY_ENABLED) {
			try {
				addBreadcrumb({
					category: "websocket",
					message: "handshake_start",
					level: "info",
					data: {
						url: this.getSanitizedUrl(urlToOpen),
						attempt: this.reconnectAttempts,
						clientConnectionAttemptId: this.currentConnectionAttemptId,
						timeSincePageLoadMs: Math.round(performance.now()),
					},
				});
			} catch {}
		}

		try {
			this.ws = new WebSocket(urlToOpen, this.wsProtocols);
			this.setupEventListeners();
		} catch (error) {
			this.log("Connection error:", error);
			this.connecting = false;
			this.scheduleReconnect();
		}
	}

	disconnect(code?: number, reason?: string) {
		this.isManuallyDisconnected = true;

		if (this.reconnectTimeoutId) {
			clearTimeout(this.reconnectTimeoutId);
			this.reconnectTimeoutId = null;
		}

		if (this.ws) {
			this.ws.close(code, reason);
		}
	}

	updateUrl(newUrl: string, reconnect = true) {
		this.wsUrl = newUrl;
		this.log("URL updated to", newUrl);

		if (reconnect && this.isConnected) {
			this.disconnect();
			this.connect();
		}
	}

	get readyState(): number {
		return this.ws?.readyState ?? WebSocket.CLOSED;
	}

	get url(): string {
		return this.ws?.url ?? this.wsUrl;
	}

	get protocol(): string {
		return this.ws?.protocol ?? "";
	}

	get extensions(): string {
		return this.ws?.extensions ?? "";
	}

	get bufferedAmount(): number {
		return this.ws?.bufferedAmount ?? 0;
	}

	get binaryType(): BinaryType {
		return this.ws?.binaryType ?? "blob";
	}

	set binaryType(value: BinaryType) {
		if (this.ws) {
			this.ws.binaryType = value;
		}
	}

	get isConnected(): boolean {
		return this.readyState === WebSocket.OPEN;
	}

	get isConnecting(): boolean {
		return this.readyState === WebSocket.CONNECTING || this.connecting;
	}

	get queuedMessages(): number {
		return this.messageQueue.length;
	}

	clearMessageQueue() {
		this.messageQueue = [];
	}

	clearPendingCalls() {
		for (const [, pending] of this.pendingCalls) {
			pending.reject(new Error("Connection closed"));
		}
		this.pendingCalls.clear();
	}

	set beforeReconnect(callback: (() => Promise<void>) | null) {
		this._beforeReconnect = callback;
	}

	/** Register an app-level hook that re-establishes the server-side session
	 * context (workspace). When set, a request that fails with WORKSPACE_REQUIRED
	 * is retried once after this resolves, so a desynced session self-heals. */
	set recoverWorkspace(callback: (() => Promise<void>) | null) {
		this._recoverWorkspace = callback;
	}

	static readonly CONNECTING = WebSocket.CONNECTING;
	static readonly OPEN = WebSocket.OPEN;
	static readonly CLOSING = WebSocket.CLOSING;
	static readonly CLOSED = WebSocket.CLOSED;
}

export const ws = new WebSocketWrapper(
	import.meta.env.VITE_WS_URL || "ws://localhost:3000/ws",
	undefined,
	{
		debug: !!import.meta.env.DEV,
	}
);

export { WebSocketWrapper };
